/**
 * Client-side exports for the inventory tabs: Excel (.xlsx built with fflate — no new
 * dependency), CSV, and a clean print view the browser can save as PDF.
 * `columns`: [{ header, value: (row) => string | number, numeric?: boolean, nowrap?: boolean }]
 * (`nowrap` keeps codes such as part numbers on one line in the print view.)
 */
import { strToU8, zipSync } from 'fflate';

const escapeXml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const columnName = (index) => {
  let name = '';
  let current = index + 1;
  while (current > 0) {
    current -= 1;
    name = String.fromCharCode(65 + (current % 26)) + name;
    current = Math.floor(current / 26);
  }
  return name;
};

const cellXml = (value, ref, numeric) => {
  if (numeric && value !== '' && value !== null && value !== undefined && Number.isFinite(Number(value))) {
    return `<c r="${ref}"><v>${Number(value)}</v></c>`;
  }
  return `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
};

export function buildXlsx(columns, rows, sheetName = 'Sheet1') {
  const header = `<row r="1">${columns.map((column, index) => cellXml(column.header, `${columnName(index)}1`, false)).join('')}</row>`;
  const body = rows.map((row, rowIndex) => {
    const r = rowIndex + 2;
    return `<row r="${r}">${columns.map((column, index) => cellXml(column.value(row), `${columnName(index)}${r}`, column.numeric)).join('')}</row>`;
  }).join('');
  const safeSheet = escapeXml(String(sheetName).replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Sheet1');
  return zipSync({
    '[Content_Types].xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
      + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
      + '<Default Extension="xml" ContentType="application/xml"/>'
      + '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>'
      + '<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>'
      + '</Types>'),
    '_rels/.rels': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
      + '</Relationships>'),
    'xl/workbook.xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
      + `<sheets><sheet name="${safeSheet}" sheetId="1" r:id="rId1"/></sheets>`
      + '</workbook>'),
    'xl/_rels/workbook.xml.rels': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
      + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>'
      + '</Relationships>'),
    'xl/worksheets/sheet1.xml': strToU8('<?xml version="1.0" encoding="UTF-8"?>'
      + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
      + `<sheetData>${header}${body}</sheetData>`
      + '</worksheet>')
  });
}

/**
 * Spreadsheet apps run a cell that starts with = + - @ (or a tab / carriage return) as a formula, so
 * text such as a part number '-' or a description '+24V…' is prefixed with a single quote. Real
 * numbers (a negative quantity) stay numbers.
 */
const FORMULA_START = /^[=+\-@\t\r]/;

const csvCell = (value) => {
  const isNumber = typeof value === 'number' && Number.isFinite(value);
  let text = String(value ?? '');
  if (!isNumber && FORMULA_START.test(text)) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function buildCsv(columns, rows) {
  const lines = [columns.map((column) => csvCell(column.header)).join(',')];
  rows.forEach((row) => lines.push(columns.map((column) => csvCell(column.value(row))).join(',')));
  return `﻿${lines.join('\r\n')}`;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export const todayStamp = () => new Date().toISOString().slice(0, 10);

const slug = (value) => String(value || 'export').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'export';

export function downloadXlsx({ filename, sheetName, columns, rows }) {
  const bytes = buildXlsx(columns, rows, sheetName);
  downloadBlob(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${slug(filename)}-${todayStamp()}.xlsx`);
}

export function downloadCsv({ filename, columns, rows }) {
  downloadBlob(new Blob([buildCsv(columns, rows)], { type: 'text/csv;charset=utf-8' }), `${slug(filename)}-${todayStamp()}.csv`);
}

/**
 * Opens a print-ready page (title, subtitle, table) and the browser's print dialog, where
 * "Save as PDF" gives the consultant-ready file. Returns false when a popup blocker stops it.
 */
export function printTable({ title, subtitle = '', columns, rows }) {
  const win = window.open('', '_blank', 'noopener=no,width=1100,height=800');
  if (!win) return false;
  const cellClass = (column) => (column.numeric ? 'n' : column.nowrap ? 'k' : '');
  const head = columns.map((column) => `<th class="${column.numeric ? 'n' : ''}">${escapeXml(column.header)}</th>`).join('');
  const body = rows.map((row) => `<tr>${columns.map((column) => `<td class="${cellClass(column)}">${escapeXml(column.value(row))}</td>`).join('')}</tr>`).join('');
  win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeXml(title)}</title>
<style>
  body { font: 11px/1.4 Manrope, system-ui, sans-serif; color: #16163f; margin: 20px; }
  h1 { font-size: 17px; margin: 0 0 4px; } p { margin: 0 0 14px; color: #686a8e; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: 10px; color: #686a8e; border-bottom: 1.5px solid #d6d8e6; padding: 5px 6px; vertical-align: bottom; }
  td { border-bottom: 1px solid #eef0f6; padding: 5px 6px; vertical-align: top; overflow-wrap: anywhere; }
  thead { display: table-header-group; } tr { break-inside: avoid; }
  .n { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; overflow-wrap: normal; }
  .k { white-space: nowrap; overflow-wrap: normal; }
  @page { size: A4 landscape; margin: 12mm; }
</style></head><body><h1>${escapeXml(title)}</h1><p>${escapeXml(subtitle)}</p>
<table><thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>
<script>window.onload = function () { window.focus(); window.print(); };</script></body></html>`);
  win.document.close();
  return true;
}
